import { useState, useCallback, useEffect } from 'react';
import { SupermarketFormat } from './CoffeeCardWeb/types';

// storage key for coffee drafts
const COFFEE_DRAFT_KEY = 'coffee_rating_draft';
// drafts auto-resume silently within this window, then expire + clear on load.
const DRAFT_EXPIRY_HOURS = 1;

// interface matching the form data structure from AddCoffeeForm
interface LocationData {
    city: string;
    country: string;
    storeCoordinates: {
        lat: number;
        lng: number;
    };
    measurementCoordinates: {
        lat: number;
        lng: number;
    };
}

interface NearbyCoffeeShop {
    _id?: string;
    name: string;
    place_id?: string;
    coordinates: {
        lat: number;
        lng: number;
    };
    distance: number;
    address: string;
    rating?: number;
    price_level?: number;
    types?: string[];
}

export interface CoffeeFormData {
    coffeeType: string;
    coffeeShop: string;
    coffeeShopRef?: string;
    coffeeShopDetails?: NearbyCoffeeShop;
    location: LocationData;
    format: { [key: string]: any };
    rating: { [key: string]: number };
    description?: string;
    quickRate?: number;
    // recorded voice description (not persisted to the draft — Blobs aren't serializable)
    voiceBlob?: Blob;
    voiceDurationMs?: number;
}

export interface CoffeeDraft {
    step: number;
    formData: CoffeeFormData;
    imagePreview: string | null; // base64 encoded image
    timestamp: number;
    coffeeShopSearch: string;
    hasGpsLocation: boolean;
    isHomeMade: boolean;
    ratingMode: 'quick' | 'detailed';
    // partial submission recovery
    coffeeId?: string; // if coffee was created but image failed
    serviceType?: 'dine-in' | 'takeaway' | 'delivery';
    // home-flow registered bean/capsule attribution (verified Coffee Masters)
    selectedMachineId?: string;
    selectedBeanId?: string;
    selectedCapsuleId?: string;
    // supermarket wizard flow (kind: 'supermarket') — brand + selected product type.
    // For an existing catalog type only `typeKey` is set; a custom type carries
    // `typeName` + `typeFormat` (and an empty/undefined typeKey).
    isSupermarket?: boolean;
    brandRef?: string;
    brandName?: string;
    typeKey?: string;
    typeName?: string;
    typeFormat?: SupermarketFormat;
}

// default form data for new entries
export const DEFAULT_FORM_DATA: CoffeeFormData = {
    coffeeType: '',
    coffeeShop: '',
    coffeeShopRef: undefined,
    coffeeShopDetails: undefined,
    location: {
        city: '',
        country: '',
        storeCoordinates: { lat: 0, lng: 0 },
        measurementCoordinates: { lat: 0, lng: 0 }
    },
    format: {},
    rating: {},
    description: '',
    quickRate: undefined
};

// check if draft is expired (older than DRAFT_EXPIRY_HOURS)
const isDraftExpired = (timestamp: number): boolean => {
    const now = Date.now();
    const expiryTime = DRAFT_EXPIRY_HOURS * 60 * 60 * 1000;
    return now - timestamp > expiryTime;
};

// convert file to base64 for storage
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

// convert base64 to file for restoration
export const base64ToFile = async (base64: string, filename: string = 'coffee.jpg'): Promise<File | null> => {
    try {
        const response = await fetch(base64);
        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    } catch (error) {
        console.error('Error converting base64 to file:', error);
        return null;
    }
};

// custom hook for managing coffee draft storage
export const useCoffeeDraft = () => {
    const [hasDraft, setHasDraft] = useState(false);
    const [draftInfo, setDraftInfo] = useState<{
        coffeeType: string;
        coffeeShop: string;
        step: number;
        timestamp: number;
        hasImage: boolean;
        coffeeId?: string;
    } | null>(null);

    // check for existing draft on mount
    useEffect(() => {
        checkForDraft();
    }, []);

    // check if a draft exists and is valid
    const checkForDraft = useCallback((): boolean => {
        try {
            const saved = localStorage.getItem(COFFEE_DRAFT_KEY);
            if (!saved) {
                setHasDraft(false);
                setDraftInfo(null);
                return false;
            }

            const draft: CoffeeDraft = JSON.parse(saved);

            // check if expired
            if (isDraftExpired(draft.timestamp)) {
                clearDraft();
                return false;
            }

            setHasDraft(true);
            setDraftInfo({
                coffeeType: draft.formData.coffeeType,
                coffeeShop: draft.formData.coffeeShop,
                step: draft.step,
                timestamp: draft.timestamp,
                hasImage: !!draft.imagePreview,
                coffeeId: draft.coffeeId
            });

            return true;
        } catch (error) {
            console.error('Error checking for draft:', error);
            setHasDraft(false);
            setDraftInfo(null);
            return false;
        }
    }, []);

    // save draft to localStorage
    const saveDraft = useCallback((draft: CoffeeDraft): void => {
        try {
            // a recorded voice Blob can't be JSON-serialized (it'd persist as `{}` and then look
            // like a real recording on restore). drop the voice fields from the saved draft.
            const { voiceBlob: _vb, voiceDurationMs: _vd, ...serializableFormData } = draft.formData;
            const draftWithTimestamp = {
                ...draft,
                formData: serializableFormData,
                timestamp: Date.now()
            };
            localStorage.setItem(COFFEE_DRAFT_KEY, JSON.stringify(draftWithTimestamp));

            setHasDraft(true);
            setDraftInfo({
                coffeeType: draft.formData.coffeeType,
                coffeeShop: draft.formData.coffeeShop,
                step: draft.step,
                timestamp: draftWithTimestamp.timestamp,
                hasImage: !!draft.imagePreview,
                coffeeId: draft.coffeeId
            });
        } catch (error) {
            console.error('Error saving draft:', error);
            // if storage is full, try to clear old data
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                // clear the image to save space and try again
                const draftWithoutImage = {
                    ...draft,
                    imagePreview: null,
                    timestamp: Date.now()
                };
                try {
                    localStorage.setItem(COFFEE_DRAFT_KEY, JSON.stringify(draftWithoutImage));
                    console.log('Saved draft without image due to storage limits');
                } catch (retryError) {
                    console.error('Failed to save draft even without image:', retryError);
                }
            }
        }
    }, []);

    // load draft from localStorage
    const loadDraft = useCallback((): CoffeeDraft | null => {
        try {
            const saved = localStorage.getItem(COFFEE_DRAFT_KEY);
            if (!saved) return null;

            const draft: CoffeeDraft = JSON.parse(saved);

            // check if expired
            if (isDraftExpired(draft.timestamp)) {
                clearDraft();
                return null;
            }

            return draft;
        } catch (error) {
            console.error('Error loading draft:', error);
            return null;
        }
    }, []);

    // clear draft from localStorage
    const clearDraft = useCallback((): void => {
        try {
            localStorage.removeItem(COFFEE_DRAFT_KEY);
            setHasDraft(false);
            setDraftInfo(null);
        } catch (error) {
            console.error('Error clearing draft:', error);
        }
    }, []);

    // update partial submission with coffeeId (for recovery after image upload failure)
    const updateDraftWithCoffeeId = useCallback((coffeeId: string): void => {
        try {
            const saved = localStorage.getItem(COFFEE_DRAFT_KEY);
            if (!saved) return;

            const draft: CoffeeDraft = JSON.parse(saved);
            draft.coffeeId = coffeeId;
            draft.timestamp = Date.now();

            localStorage.setItem(COFFEE_DRAFT_KEY, JSON.stringify(draft));

            setDraftInfo(prev => prev ? { ...prev, coffeeId } : null);
        } catch (error) {
            console.error('Error updating draft with coffeeId:', error);
        }
    }, []);

    // get formatted time since draft was saved
    const getDraftAge = useCallback((): string => {
        if (!draftInfo) return '';

        const ageMs = Date.now() - draftInfo.timestamp;
        const minutes = Math.floor(ageMs / (1000 * 60));
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else if (minutes > 0) {
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        } else {
            return 'just now';
        }
    }, [draftInfo]);

    return {
        hasDraft,
        draftInfo,
        saveDraft,
        loadDraft,
        clearDraft,
        checkForDraft,
        updateDraftWithCoffeeId,
        getDraftAge,
        fileToBase64,
        base64ToFile
    };
};

export default useCoffeeDraft;
